import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, PackageCheck, Plus, Search, Trash2, UploadCloud, X } from "lucide-react";
import { request } from "./appApi";

export type ShipmentEvent = {
  id: number;
  event_time?: string | null;
  status_code?: string;
  status_name?: string;
  description: string;
  location?: string | null;
};

export type OrderShipment = {
  id: number;
  order_report_id: number;
  report_no?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  camp_period?: string;
  class_name?: string;
  package_type: string;
  package_type_name: string;
  package_label?: string | null;
  tracking_no: string;
  carrier_code?: string | null;
  carrier_name: string;
  receiver_phone_suffix?: string | null;
  status_code: string;
  status_name: string;
  latest_event_desc?: string | null;
  latest_event_time?: string | null;
  last_synced_at?: string | null;
  error_message?: string | null;
  note?: string | null;
  events?: ShipmentEvent[];
};

export type ShipmentSummaryItem = Pick<OrderShipment, "id" | "package_type" | "package_type_name" | "carrier_name" | "tracking_no" | "status_code" | "status_name" | "latest_event_time">;

type Option = { value: string; label: string };
type ShipmentConfig = {
  configured: boolean;
  provider: string;
  package_types: Option[];
  carriers: Option[];
  query_cache_minutes: number;
};

type CandidateOrder = {
  id: number;
  report_no?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  camp_period?: string;
  class_name?: string;
  status?: string;
  refund_type?: string;
};

type PreviewRow = {
  row_no: number;
  child_name: string;
  tracking_no: string;
  package_type: string;
  package_type_name: string;
  carrier_code?: string;
  carrier_name?: string;
  order_report_id?: number | null;
  status: "matched" | "ambiguous" | "unmatched" | "duplicate" | "invalid";
  message: string;
  candidates: CandidateOrder[];
};

type ShipmentImportException = {
  row_no: number;
  child_name: string;
  tracking_no: string;
  package_type_name: string;
  carrier_name: string;
  status: PreviewRow["status"] | "commit_error";
  message: string;
};

type ShipmentImportResult = {
  createdCount: number;
  exceptions: ShipmentImportException[];
};

type PageResult<T> = { items: T[]; total: number; page: number; per_page: number; pages: number };

const IMPORT_REQUEST_BATCH_SIZE = 800;
const PREVIEW_PAGE_SIZE = 200;

const fallbackPackageTypes: Option[] = [
  { value: "album", label: "相册" },
  { value: "gift", label: "赠品" },
  { value: "album_gift", label: "相册＋赠品" },
  { value: "reship", label: "补发" },
  { value: "return", label: "退货" },
  { value: "other", label: "其他" },
];

function queryString(params: Record<string, unknown>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

function importColumnLines(text: string, headerPattern: RegExp) {
  const lines = text.replace(/\r/g, "").split("\n").map(line => line.trim());
  while (lines.length && !lines[lines.length - 1]) lines.pop();
  if (lines.length && headerPattern.test(lines[0])) lines.shift();
  return lines;
}

function resolveCarrier(value: string, carriers: Option[]) {
  const input = value.trim();
  if (!input) return {};
  const normalized = input.toLowerCase().replace(/[\s快递速递物流]/g, "");
  const carrier = carriers.find(item => item.value.toLowerCase() === input.toLowerCase()
    || item.label === input
    || item.label.toLowerCase().replace(/[\s快递速递物流]/g, "") === normalized);
  return carrier
    ? { carrier_code: carrier.value, carrier_name: carrier.label }
    : { carrier_name: input };
}

function splitIntoBatches<T>(items: T[], size = IMPORT_REQUEST_BATCH_SIZE) {
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) batches.push(items.slice(index, index + size));
  return batches;
}

function normalizedTrackingNo(value: string) {
  return value.replace(/\s+/g, "").toUpperCase();
}

function parseImportColumns(nameText: string, trackingText: string, carrierText: string, packageType: string, carriers: Option[]) {
  const names = importColumnLines(nameText, /^(孩子|小孩|学员)?姓名$/);
  const trackingNumbers = importColumnLines(trackingText, /^(快递|物流)?单号$/);
  const carrierNames = importColumnLines(carrierText, /^(快递|物流)?公司$|^承运商$/);
  const rowCount = Math.max(names.length, trackingNumbers.length, carrierNames.length);
  const items = Array.from({ length: rowCount }, (_, index) => ({
    row_no: index + 1,
    child_name: names[index] || "",
    tracking_no: trackingNumbers[index] || "",
    package_type: packageType,
    ...resolveCarrier(carrierNames[index] || "", carriers),
  }));
  const incompleteRows = items.filter(item => !item.child_name || !item.tracking_no || !item.carrier_name).map(item => item.row_no);
  return {
    items,
    counts: { names: names.length, trackingNumbers: trackingNumbers.length, carrierNames: carrierNames.length },
    rowCount,
    incompleteRows,
    hasCountMismatch: rowCount > 0 && new Set([names.length, trackingNumbers.length, carrierNames.length]).size > 1,
  };
}

function cleanExportCell(value: unknown) {
  return String(value ?? "").replace(/[\t\r\n]+/g, " ").trim();
}

function exceptionStatusName(status: ShipmentImportException["status"]) {
  return ({
    ambiguous: "同名待选择",
    unmatched: "未匹配订单",
    duplicate: "重复单号",
    invalid: "格式错误",
    matched: "匹配异常",
    commit_error: "写入失败",
  } as const)[status] || "其他异常";
}

export function getShipmentSummaries(orderIds: Array<number | string>) {
  if (!orderIds.length) return Promise.resolve({} as Record<string, ShipmentSummaryItem[]>);
  return request<Record<string, ShipmentSummaryItem[]>>(`/shipments/summaries${queryString({ order_ids: orderIds.join(",") })}`);
}

export function ShipmentSummary({ items = [] }: { items?: ShipmentSummaryItem[] }) {
  if (!items.length) return <span className="shipment-summary-empty">暂无快递</span>;
  return <div className="shipment-summary-list">{items.slice(0, 3).map(item => (
    <span key={item.id} className="shipment-number-summary">
      {item.package_type_name} · {item.tracking_no}
    </span>
  ))}{items.length > 3 && <small>另有 {items.length - 3} 个包裹</small>}</div>;
}

export function OrderShipmentPanel({ orderId }: { orderId: number | string }) {
  const [rows, setRows] = useState<OrderShipment[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    request<OrderShipment[]>(`/shipments/order/${orderId}`)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [orderId]);
  if (loading) return <div className="shipment-panel-empty">正在加载快递单号…</div>;
  if (!rows.length) return <div className="shipment-panel-empty">这个订单暂未绑定快递单号</div>;
  return <div className="order-shipment-cards">{rows.map(item => (
    <article key={item.id} className="order-shipment-card">
      <div className="order-shipment-head">
        <div><b>{item.package_type_name}</b><span>{item.carrier_name}</span></div>
      </div>
      <div className="shipment-number">{item.tracking_no}</div>
      {item.note && <p>{item.note}</p>}
    </article>
  ))}</div>;
}

type ShipmentEntryRow = {
  key: string;
  tracking_no: string;
  package_type: string;
  carrier_code: string;
  error?: string;
};

function newShipmentEntry(): ShipmentEntryRow {
  return {
    key: `shipment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    tracking_no: "",
    package_type: "album",
    carrier_code: "",
  };
}

export function OrderShipmentEntryModal({
  orderId,
  childName,
  close,
  toast,
  saved,
}: {
  orderId: number | string;
  childName?: string;
  close: () => void;
  toast: (message: string) => void;
  saved: (created: number) => void | Promise<void>;
}) {
  const [config, setConfig] = useState<ShipmentConfig>();
  const [existing, setExisting] = useState<OrderShipment[]>([]);
  const [rows, setRows] = useState<ShipmentEntryRow[]>([newShipmentEntry()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const packageTypes = config?.package_types?.length ? config.package_types : fallbackPackageTypes;
  const carriers = config?.carriers || [];

  useEffect(() => {
    Promise.all([
      request<ShipmentConfig>("/shipments/config"),
      request<OrderShipment[]>(`/shipments/order/${orderId}`),
    ]).then(([nextConfig, currentRows]) => {
      setConfig(nextConfig);
      setExisting(currentRows);
    }).catch((error) => toast((error as Error).message || "快递信息加载失败"))
      .finally(() => setLoading(false));
  }, [orderId]);

  const updateRow = (key: string, field: keyof ShipmentEntryRow, value: string) => {
    setRows(current => current.map(row => row.key === key ? { ...row, [field]: value, error: undefined } : row));
  };

  const removeRow = (key: string) => {
    setRows(current => current.length > 1 ? current.filter(row => row.key !== key) : [newShipmentEntry()]);
  };

  const submit = async () => {
    const pending = rows.filter(row => row.tracking_no.trim());
    if (!pending.length) return toast("请至少填写一个快递单号");
    const normalized = pending.map(row => row.tracking_no.trim().toUpperCase().replace(/\s+/g, ""));
    if (new Set(normalized).size !== normalized.length) return toast("本次录入存在重复快递单号，请检查");
    setSaving(true);
    try {
      const result = await request<{
        created: OrderShipment[];
        errors: Array<{ row_no: number; tracking_no: string; message: string }>;
      }>("/admin/shipments/batch-create", {
        method: "POST",
        body: JSON.stringify({
          package_type: "album",
          items: pending.map((row, index) => {
            const carrier = carriers.find(item => item.value === row.carrier_code);
            return {
              row_no: index + 1,
              child_name: childName || "",
              order_report_id: Number(orderId),
              tracking_no: row.tracking_no.trim(),
              package_type: row.package_type,
              carrier_code: row.carrier_code || undefined,
              carrier_name: carrier?.label,
            };
          }),
        }),
      });
      if (result.created.length) {
        await saved(result.created.length);
        setExisting(current => [...result.created, ...current]);
      }
      if (result.errors.length) {
        const failedByRow = new Map(result.errors.map(error => [error.row_no, error.message]));
        setRows(pending.map((row, index) => ({ ...row, error: failedByRow.get(index + 1) })).filter(row => row.error));
        toast(`成功录入 ${result.created.length} 个单号，${result.errors.length} 个未录入`);
      } else {
        toast(`成功录入 ${result.created.length} 个快递单号，订单已更新为已发货`);
        close();
      }
    } catch (error) {
      toast((error as Error).message || "快递单号录入失败");
    } finally {
      setSaving(false);
    }
  };

  return <div className="modal-backdrop">
    <div className="modal shipment-entry-modal">
      <div className="modal-head">
        <div><span className="eyebrow">ORDER SHIPPING</span><h2>录入快递单号</h2><p>{childName || `订单 #${orderId}`} · 可一次录入多个不同类型的包裹</p></div>
        <button className="icon-btn" onClick={close}><X size={18} /></button>
      </div>
      {loading ? <div className="shipment-panel-empty">正在加载快递信息…</div> : <>
        {!!existing.length && <section className="shipment-entry-existing">
          <div><b>已绑定单号</b><span>{existing.length} 个包裹</span></div>
          <div>{existing.map(item => <span key={item.id}><b>{item.package_type_name}</b><code>{item.tracking_no}</code></span>)}</div>
        </section>}
        <div className="shipment-entry-table">
          <div className="shipment-entry-table-head"><b>新增包裹</b><button className="secondary" onClick={() => setRows(current => [...current, newShipmentEntry()])}><Plus size={15} />添加一行</button></div>
          {rows.map((row, index) => <div className={`shipment-entry-row ${row.error ? "error" : ""}`} key={row.key}>
            <span className="shipment-entry-index">{index + 1}</span>
            <label><span>单号类型</span><select value={row.package_type} onChange={event => updateRow(row.key, "package_type", event.target.value)}>{packageTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <label className="shipment-entry-number"><span>快递单号</span><input value={row.tracking_no} onChange={event => updateRow(row.key, "tracking_no", event.target.value)} placeholder="请输入或粘贴单号" /></label>
            <label><span>快递公司</span><select value={row.carrier_code} onChange={event => updateRow(row.key, "carrier_code", event.target.value)}><option value="">自动识别/待确认</option>{carriers.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <button className="shipment-entry-remove" title="删除这一行" onClick={() => removeRow(row.key)}><Trash2 size={15} /></button>
            {row.error && <small>{row.error}</small>}
          </div>)}
        </div>
        <div className="shipment-entry-note">保存后会写入快递管理使用的同一张数据表，订单进度和 H5 将立即显示“已发货”。</div>
        <div className="modal-actions"><button className="secondary" onClick={close}>取消</button><button className="primary" disabled={saving} onClick={submit}>{saving ? "正在保存…" : `保存 ${rows.filter(row => row.tracking_no.trim()).length || ""} 个单号`}</button></div>
      </>}
    </div>
  </div>;
}

export function LogisticsManagementPage({ toast, onBack }: { toast: (message: string) => void; onBack?: () => void }) {
  const [config, setConfig] = useState<ShipmentConfig>();
  const [nameText, setNameText] = useState("");
  const [trackingText, setTrackingText] = useState("");
  const [carrierText, setCarrierText] = useState("");
  const [packageType, setPackageType] = useState("album");
  const [matchFilters, setMatchFilters] = useState({ brand: "", campus: "", camp_period: "", class_name: "", start_at: "", end_at: "" });
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewing, setPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState("");
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [importResult, setImportResult] = useState<ShipmentImportResult | null>(null);
  const [filters, setFilters] = useState({ keyword: "", package_type: "", brand: "", campus: "" });
  const [page, setPage] = useState(1);
  const [list, setList] = useState<PageResult<OrderShipment>>({ items: [], total: 0, page: 1, per_page: 50, pages: 1 });
  const [loading, setLoading] = useState(false);

  const packageTypes = config?.package_types?.length ? config.package_types : fallbackPackageTypes;
  const carriers = config?.carriers || [];
  const parsedImport = useMemo(
    () => parseImportColumns(nameText, trackingText, carrierText, packageType, carriers),
    [nameText, trackingText, carrierText, packageType, carriers],
  );
  const parsedCount = parsedImport.rowCount;
  const importBatchCount = Math.max(1, Math.ceil(parsedCount / IMPORT_REQUEST_BATCH_SIZE));
  const importInputError = parsedImport.hasCountMismatch
    ? `三列行数不一致：姓名 ${parsedImport.counts.names} 行、单号 ${parsedImport.counts.trackingNumbers} 行、快递公司 ${parsedImport.counts.carrierNames} 行`
    : parsedImport.incompleteRows.length
      ? `第 ${parsedImport.incompleteRows.slice(0, 6).join("、")}${parsedImport.incompleteRows.length > 6 ? "…" : ""} 行存在空白，请补齐三列内容`
      : "";
  const previewPages = Math.max(1, Math.ceil(previewRows.length / PREVIEW_PAGE_SIZE));
  const visiblePreviewRows = useMemo(
    () => previewRows.slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE),
    [previewRows, previewPage],
  );

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const data = await request<PageResult<OrderShipment>>(`/admin/shipments${queryString({ ...filters, page: targetPage, per_page: 50 })}`);
      setList(data);
      setPage(data.page);
    } catch (error) {
      toast((error as Error).message || "快递列表加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    request<ShipmentConfig>("/shipments/config").then(setConfig).catch(() => undefined);
    void load(1);
  }, []);

  const preview = async () => {
    const items = parsedImport.items;
    if (!items.length) return toast("请先填写孩子姓名、快递单号和快递公司");
    if (importInputError) return toast(importInputError);
    setPreviewing(true);
    try {
      const batches = splitIntoBatches(items);
      const rows: PreviewRow[] = [];
      for (let index = 0; index < batches.length; index += 1) {
        setPreviewProgress(`${index + 1}/${batches.length}`);
        const data = await request<{ rows: PreviewRow[] }>("/admin/shipments/batch-preview", {
          method: "POST",
          body: JSON.stringify({
            items: batches[index],
            package_type: packageType,
            filters: matchFilters,
          }),
        });
        rows.push(...data.rows);
      }
      const trackingCounts = new Map<string, number>();
      items.forEach(item => {
        const trackingNo = normalizedTrackingNo(item.tracking_no);
        trackingCounts.set(trackingNo, (trackingCounts.get(trackingNo) || 0) + 1);
      });
      setPreviewRows(rows.map(row => (trackingCounts.get(normalizedTrackingNo(row.tracking_no)) || 0) > 1
        ? { ...row, status: "duplicate", message: "本批次快递单号重复", order_report_id: null }
        : row));
      setPreviewPage(1);
    } catch (error) {
      toast((error as Error).message || "匹配预览失败");
    } finally {
      setPreviewing(false);
      setPreviewProgress("");
    }
  };

  const selectCandidate = (rowNo: number, orderId: number) => {
    setPreviewRows(current => current.map(row => row.row_no === rowNo
      ? { ...row, order_report_id: orderId || null, status: orderId ? "matched" : "ambiguous", message: orderId ? "已人工选择订单" : "请选择订单" }
      : row));
  };

  const previewExceptions = () => previewRows
    .filter(row => row.status !== "matched" || !row.order_report_id)
    .map<ShipmentImportException>(row => ({
      row_no: row.row_no,
      child_name: row.child_name,
      tracking_no: row.tracking_no,
      package_type_name: row.package_type_name,
      carrier_name: row.carrier_name || "未填写",
      status: row.status,
      message: row.message,
    }));

  const exportImportExceptions = (exceptions = importResult?.exceptions || []) => {
    if (!exceptions.length) return toast("本次导入没有异常记录");
    const rows = [
      ["原行号", "孩子姓名", "异常快递单号", "包裹类型", "快递公司", "异常类型", "异常原因"],
      ...exceptions.map(item => [
        item.row_no,
        item.child_name,
        item.tracking_no,
        item.package_type_name,
        item.carrier_name,
        exceptionStatusName(item.status),
        item.message,
      ]),
    ];
    const content = rows.map(row => row.map(cleanExportCell).join("\t")).join("\n");
    const blob = new Blob(["\ufeff", content], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `快递单号导入异常-${new Date().toISOString().slice(0, 10)}.tsv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const confirmImport = async () => {
    const skippedBeforeCommit = previewExceptions();
    const items = previewRows.filter(row => row.status === "matched" && row.order_report_id).map(row => ({
      row_no: row.row_no,
      child_name: row.child_name,
      tracking_no: row.tracking_no,
      order_report_id: row.order_report_id,
      package_type: row.package_type,
      carrier_code: row.carrier_code || undefined,
      carrier_name: row.carrier_name,
    }));
    if (!items.length) {
      if (skippedBeforeCommit.length) {
        setImportResult({ createdCount: 0, exceptions: skippedBeforeCommit });
        return;
      }
      return toast("没有可以导入的匹配记录");
    }
    setImporting(true);
    try {
      const batches = splitIntoBatches(items);
      const created: OrderShipment[] = [];
      const errors: Array<{ row_no: number; child_name: string; tracking_no: string; message: string }> = [];
      for (let index = 0; index < batches.length; index += 1) {
        const batch = batches[index];
        setImportProgress(`${index + 1}/${batches.length}`);
        try {
          const result = await request<{
            created: OrderShipment[];
            errors: Array<{ row_no: number; child_name: string; tracking_no: string; message: string }>;
          }>("/admin/shipments/batch-create", {
            method: "POST",
            body: JSON.stringify({ items: batch, package_type: packageType }),
          });
          created.push(...result.created);
          errors.push(...result.errors);
        } catch (error) {
          const message = (error as Error).message || "这一批写入失败";
          errors.push(...batch.map(item => ({ row_no: item.row_no, child_name: item.child_name, tracking_no: item.tracking_no, message })));
        }
      }
      const itemByRow = new Map(items.map(item => [item.row_no, item]));
      const commitExceptions = errors.map<ShipmentImportException>(error => {
        const source = itemByRow.get(error.row_no);
        const sourcePreview = previewRows.find(row => row.row_no === error.row_no);
        return {
          row_no: error.row_no,
          child_name: error.child_name || source?.child_name || "",
          tracking_no: error.tracking_no || source?.tracking_no || "",
          package_type_name: sourcePreview?.package_type_name || packageTypes.find(item => item.value === source?.package_type)?.label || source?.package_type || "",
          carrier_name: sourcePreview?.carrier_name || "未填写",
          status: "commit_error",
          message: error.message || "写入失败",
        };
      });
      const exceptions = [...skippedBeforeCommit, ...commitExceptions].sort((a, b) => a.row_no - b.row_no);
      toast(`成功导入 ${created.length} 个快递单号${exceptions.length ? `，${exceptions.length} 条异常未导入` : ""}`);
      setNameText("");
      setTrackingText("");
      setCarrierText("");
      setPreviewRows([]);
      await load(1);
      if (exceptions.length) setImportResult({ createdCount: created.length, exceptions });
    } catch (error) {
      toast((error as Error).message || "批量导入失败");
    } finally {
      setImporting(false);
      setImportProgress("");
    }
  };

  const changeCarrier = async (item: OrderShipment, carrierCode: string) => {
    const carrier = carriers.find(value => value.value === carrierCode);
    try {
      await request(`/admin/shipments/${item.id}`, {
        method: "PUT",
        body: JSON.stringify({ carrier_code: carrierCode, carrier_name: carrier?.label || "" }),
      });
      toast(carrierCode ? `已设置快递公司：${carrier?.label || carrierCode}` : "已改为自动识别快递公司");
      await load(page);
    } catch (error) {
      toast((error as Error).message || "快递公司保存失败");
    }
  };

  const remove = async (item: OrderShipment) => {
    if (!window.confirm(`确认解绑 ${item.child_name || "该订单"} 的快递单号 ${item.tracking_no}？`)) return;
    try {
      await request(`/admin/shipments/${item.id}`, { method: "DELETE" });
      toast("快递单号已解绑");
      await load();
    } catch (error) {
      toast((error as Error).message || "解绑失败");
    }
  };

  return <div className="logistics-management-page">
    <div className="logistics-topbar">
      {onBack && <button className="secondary" onClick={onBack}>返回订单总览</button>}
      <div className="logistics-provider-state ready">只保存订单绑定关系、包裹类型和快递单号</div>
    </div>

    <section className="logistics-import-card">
      <div className="logistics-section-title"><div><UploadCloud /><h3>批量导入快递单号</h3></div><span>只按姓名匹配订单，单号和快递公司按行绑定</span></div>
      <div className="logistics-import-settings">
        <label><span>本批包裹类型</span><select value={packageType} onChange={event => { setPackageType(event.target.value); setPreviewRows([]); }}>{packageTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label><span>品牌范围</span><input value={matchFilters.brand} onChange={event => setMatchFilters(value => ({ ...value, brand: event.target.value }))} placeholder="可选，减少重名" /></label>
        <label><span>校区范围</span><input value={matchFilters.campus} onChange={event => setMatchFilters(value => ({ ...value, campus: event.target.value }))} placeholder="可选" /></label>
        <label><span>营期范围</span><input value={matchFilters.camp_period} onChange={event => setMatchFilters(value => ({ ...value, camp_period: event.target.value }))} placeholder="可选" /></label>
        <label><span>班级范围</span><input value={matchFilters.class_name} onChange={event => setMatchFilters(value => ({ ...value, class_name: event.target.value }))} placeholder="可选" /></label>
      </div>
      <div className="logistics-import-columns">
        <label className="logistics-import-column" htmlFor="shipment-import-names">
          <span><b>1. 孩子姓名 <em>*</em></b><small>{parsedImport.counts.names} 行</small></span>
          <textarea id="shipment-import-names" rows={8} value={nameText} onChange={event => { setNameText(event.target.value); setPreviewRows([]); }} placeholder={"每行一个名字，例如：\n张小明\n李小雨\n王小乐"} aria-invalid={!!importInputError} />
        </label>
        <label className="logistics-import-column" htmlFor="shipment-import-tracking">
          <span><b>2. 快递单号 <em>*</em></b><small>{parsedImport.counts.trackingNumbers} 行</small></span>
          <textarea id="shipment-import-tracking" rows={8} value={trackingText} onChange={event => { setTrackingText(event.target.value); setPreviewRows([]); }} placeholder={"与姓名逐行对应，例如：\nSF1234567890\nYT1234567890\n773123456789"} aria-invalid={!!importInputError} />
        </label>
        <label className="logistics-import-column" htmlFor="shipment-import-carriers">
          <span><b>3. 快递公司 <em>*</em></b><small>{parsedImport.counts.carrierNames} 行</small></span>
          <textarea id="shipment-import-carriers" rows={8} value={carrierText} onChange={event => { setCarrierText(event.target.value); setPreviewRows([]); }} placeholder={"与姓名逐行对应，例如：\n顺丰速运\n圆通速递\n中通快递"} aria-invalid={!!importInputError} />
        </label>
      </div>
      <div className={`logistics-import-validation ${importInputError ? "error" : parsedCount ? "ready" : ""}`} role="status">
        {importInputError || (parsedCount
          ? `已读取 ${parsedCount} 行${importBatchCount > 1 ? `，系统将自动分成 ${importBatchCount} 批处理，无需手动拆分` : ""}`
          : "请分别从 Excel 复制三列内容；系统会将同一行的单号和快递公司绑定到姓名匹配的订单")}
      </div>
      <div className="logistics-import-actions"><span>识别到 {parsedCount} 行</span><button className="primary" disabled={previewing || !parsedCount || !!importInputError} onClick={preview}>{previewing ? `正在匹配 ${previewProgress}` : "预览匹配结果"}</button></div>
    </section>

    {!!previewRows.length && <section className="logistics-preview-card">
      <div className="logistics-section-title"><div><PackageCheck /><h3>导入预览</h3></div><span>同名订单必须人工确认，冲突数据不会写入</span></div>
      <div className="logistics-preview-summary">
        {(["matched", "ambiguous", "unmatched", "duplicate", "invalid"] as const).map(status => <span key={status} className={status}>{previewRows.filter(row => row.status === status).length}<small>{{ matched: "可导入", ambiguous: "待选择", unmatched: "未匹配", duplicate: "重复", invalid: "格式错误" }[status]}</small></span>)}
      </div>
      <div className="table-card"><table><thead><tr><th>行</th><th>小孩</th><th>快递单号</th><th>快递公司</th><th>类型</th><th>匹配订单</th><th>结果</th></tr></thead><tbody>{visiblePreviewRows.map(row => <tr key={row.row_no}>
        <td>{row.row_no}</td><td><b>{row.child_name}</b></td><td>{row.tracking_no}</td><td>{row.carrier_name || "-"}</td><td>{row.package_type_name}</td>
        <td>{row.candidates.length > 1 ? <select value={row.order_report_id || ""} onChange={event => selectCandidate(row.row_no, Number(event.target.value))}><option value="">请选择同名订单</option>{row.candidates.map(order => <option key={order.id} value={order.id}>{order.report_no} · {order.brand || "-"}/{order.campus || "-"}/{order.camp_period || order.class_name || "-"}</option>)}</select> : row.candidates[0] ? `${row.candidates[0].report_no} · ${row.candidates[0].brand || "-"}/${row.candidates[0].campus || "-"}` : "-"}</td>
        <td><span className={`preview-status ${row.status}`}>{row.message}</span></td>
      </tr>)}</tbody></table></div>
      {previewRows.length > PREVIEW_PAGE_SIZE && <div className="logistics-pagination"><button disabled={previewPage <= 1} onClick={() => setPreviewPage(value => value - 1)}>上一页</button><span>预览第 {previewPage} / {previewPages} 页，每页 {PREVIEW_PAGE_SIZE} 行</span><button disabled={previewPage >= previewPages} onClick={() => setPreviewPage(value => value + 1)}>下一页</button></div>}
      <div className="logistics-import-actions"><span>将导入 {previewRows.filter(row => row.status === "matched" && row.order_report_id).length} 条</span><button className="primary" disabled={importing} onClick={confirmImport}>{importing ? `正在导入 ${importProgress}` : previewRows.some(row => row.status === "matched" && row.order_report_id) ? "确认导入可匹配记录" : "查看并导出异常"}</button></div>
    </section>}

    <section className="logistics-list-card">
      <div className="logistics-section-title"><div><PackageCheck /><h3>快递记录</h3></div><span>共 {list.total} 个包裹</span></div>
      <div className="logistics-list-filters">
        <label className="logistics-search"><Search size={16} /><input value={filters.keyword} onChange={event => setFilters(value => ({ ...value, keyword: event.target.value }))} placeholder="姓名、订单号、快递单号" /></label>
        <select value={filters.package_type} onChange={event => setFilters(value => ({ ...value, package_type: event.target.value }))}><option value="">全部包裹类型</option>{packageTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
        <input value={filters.brand} onChange={event => setFilters(value => ({ ...value, brand: event.target.value }))} placeholder="品牌" />
        <button className="secondary" onClick={() => load(1)}><Search size={15} />查询</button>
      </div>
      {loading ? <div className="shipment-panel-empty">正在加载快递记录…</div> : list.items.length ? <div className="table-card logistics-table"><table><thead><tr><th>订单/小孩</th><th>包裹</th><th>快递公司与单号</th><th>操作</th></tr></thead><tbody>{list.items.map(item => <tr key={item.id}>
        <td><b>{item.child_name || "-"}</b><span>{item.report_no || `#${item.order_report_id}`}</span><small>{item.brand || "-"} / {item.campus || "-"}</small></td>
        <td><span className="package-type-badge">{item.package_type_name}</span></td>
        <td><select className="shipment-carrier-select" value={item.carrier_code || ""} onChange={event => void changeCarrier(item, event.target.value)}><option value="">待确认快递公司</option>{carriers.map(carrier => <option key={carrier.value} value={carrier.value}>{carrier.label}</option>)}</select><code>{item.tracking_no}</code></td>
        <td className="row-actions"><button title="解绑" className="danger" onClick={() => remove(item)}><Trash2 size={15} /></button></td>
      </tr>)}</tbody></table></div> : <div className="shipment-panel-empty">暂无符合条件的快递记录</div>}
      <div className="logistics-pagination"><button disabled={page <= 1} onClick={() => load(page - 1)}>上一页</button><span>第 {page} / {list.pages} 页</span><button disabled={page >= list.pages} onClick={() => load(page + 1)}>下一页</button></div>
    </section>

    {importResult && <div className="modal-backdrop">
      <div className="modal shipment-import-result-modal">
        <div className="modal-head">
          <div><span className="eyebrow">IMPORT RESULT</span><h2>快递单号导入结果</h2><p>成功记录已经保存，异常记录未写入系统，可导出后核对处理。</p></div>
          <button className="icon-btn" onClick={() => setImportResult(null)}><X size={18} /></button>
        </div>
        <div className="shipment-import-result-summary">
          <span className="success"><PackageCheck size={20} /><small>成功导入</small><b>{importResult.createdCount}</b></span>
          <span className="error"><AlertTriangle size={20} /><small>异常未导入</small><b>{importResult.exceptions.length}</b></span>
        </div>
        <div className="shipment-import-result-tip"><AlertTriangle size={16} /><span>异常名单包含未匹配、同名未选择、重复、格式错误和确认写入失败的记录。</span></div>
        <div className="shipment-import-exception-table"><table><thead><tr><th>原行</th><th>孩子</th><th>异常单号</th><th>快递公司</th><th>类型</th><th>异常原因</th></tr></thead><tbody>{importResult.exceptions.map((item, index) => <tr key={`${item.row_no}-${item.tracking_no}-${index}`}>
          <td>{item.row_no}</td><td><b>{item.child_name || "-"}</b></td><td><code>{item.tracking_no || "-"}</code></td><td>{item.carrier_name || "-"}</td><td>{exceptionStatusName(item.status)}</td><td><span>{item.message}</span></td>
        </tr>)}</tbody></table></div>
        <div className="modal-actions">
          <button className="secondary" onClick={() => setImportResult(null)}>关闭</button>
          <button className="primary" onClick={() => exportImportExceptions()}><Download size={16} />导出异常单号</button>
        </div>
      </div>
    </div>}
  </div>;
}
